import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Btn } from '../ui/Btn.jsx';
import { SKILL_MAP } from '../../lib/bkt.js';
import { getSpellingMastery } from '../../lib/spelling.js';
import { canAccessStories, getTodayStory } from '../../lib/stories.js';
import { rqConfettiBurst } from '../../lib/animations.js';
import { getLevelName, getMasteryLabel } from '../../config/levelConfig.js';
import { PinGate } from '../parent/PinGate.jsx';
import { ParentCoachingPrompt } from '../parent/ParentCoachingPrompt.jsx';
import { getNextCoachingPrompt, logCoachingDone } from '../../lib/coachingPrompts.js';
import { fadeIn, popIn, scaleIn, slideUp, staggerChild, staggerContainer, tapBounce, tapScale } from '../../lib/motionPresets.js';

export function SessionDone({name,stars,minutesPracticed,aiProfile,onUnlock,tracker,age,parentPin,expired,childId,sessionSummary,onOpenStories,onExtraPractice,onSetPin,onViewDashboard,coachingPromptsEnabled=true}) {
  const [parentUnlocked, setParentUnlocked] = useState(false);
  const [showPinEntry, setShowPinEntry] = useState(false);
  const [showNoticed, setShowNoticed] = useState(false);
  const [coachingPrompt, setCoachingPrompt] = useState(null);
  const [showCoaching, setShowCoaching] = useState(false);
  const recent = (tracker?.log || []).slice(-20);
  const correct = recent.filter((r) => r.c).length;
  const total = recent.length;
  const acc = total > 0 ? Math.round((correct / total) * 100) : 0;
  const minDisplay = Math.max(1, minutesPracticed || 0);
  const completedActivities = sessionSummary?.activitiesCompleted || 0;
  const sessionAttempted = sessionSummary?.wordsAttempted || total;
  const sessionCorrect = sessionSummary?.wordsCorrect || correct;
  const sessionScore = sessionSummary?.score || stars;
  const sessionAccuracy = sessionAttempted > 0 ? Math.round((sessionCorrect / sessionAttempted) * 100) : acc;
  const bkt = tracker?.bkt || {};
  const curSkills = SKILL_MAP[tracker?.currentLevel] || [];
  const lowestSkill = curSkills.reduce((low, sk) => (!low || (bkt[sk] || 0) < (bkt[low] || 0)) ? sk : low, null);
  const spellingSummary = getSpellingMastery(childId, tracker?.wordMastery);
  const currentLevelName = getLevelName(tracker?.currentLevel || 1);
  const nextLevelName = getLevelName(Math.min((tracker?.currentLevel || 1) + 1, 15));
  const weakestSkillProb = lowestSkill ? (bkt[lowestSkill] || 0) : null;
  const weakestSkillMastery = weakestSkillProb != null ? getMasteryLabel(weakestSkillProb) : null;

  useEffect(() => {
    rqConfettiBurst({ count: 25, spread: 70 });
    const t = setTimeout(() => setShowNoticed(true), 1400);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!coachingPromptsEnabled) return undefined;
    const timer = setTimeout(() => {
      const prompt = getNextCoachingPrompt();
      if (prompt) {
        setCoachingPrompt(prompt);
        setShowCoaching(true);
      }
    }, 1800);
    return () => clearTimeout(timer);
  }, [coachingPromptsEnabled]);

  const handleParentView = () => {
    if (!parentPin) setParentUnlocked(true);
    else setShowPinEntry(true);
  };

  const msg = sessionAccuracy >= 90 ? 'Amazing job!' : sessionAccuracy >= 70 ? 'Great practice today!' : "You're getting stronger!";
  const noticedInsights = useMemo(() => {
    try {
      const skillName = lowestSkill ? lowestSkill.replace(/_/g, ' ') : 'reading skills';
      const accuracyInsight = sessionAttempted > 0
        ? sessionAccuracy >= 85
          ? `Your child got ${sessionCorrect} out of ${sessionAttempted} responses correct. Finley is preparing slightly harder content in the next session. This means your child is showing strong foundations and is ready for a new challenge.`
          : sessionAccuracy >= 65
            ? `Your child got ${sessionCorrect} out of ${sessionAttempted} responses correct. Finley is keeping the challenge level steady while reinforcing key skills. This helps your child grow confidence without feeling overwhelmed.`
            : `Your child got ${sessionCorrect} out of ${sessionAttempted} responses correct. Finley is scheduling more targeted practice at the current level. This helps your child strengthen core reading patterns before moving ahead.`
        : `Your child completed today's activities and gave Finley fresh learning data. Finley is tuning the next session to reinforce the right skills. This keeps progress steady and personalized.`;

      const consistencyInsight = completedActivities > 0
        ? `Your child completed all ${completedActivities} planned activities in about ${minDisplay} minutes. Finley is using this consistency to build tomorrow's session plan. Consistent practice is one of the strongest predictors of reading growth.`
        : `Your child completed today's session and gave Finley meaningful practice data. Finley is tracking this effort and building from it. Frequent short sessions lead to stronger long-term gains.`;

      const skillInsight = lowestSkill && weakestSkillMastery
        ? `Your child showed progress in ${skillName}. Finley marked this area as ${weakestSkillMastery.label} and is tailoring the next activities toward ${nextLevelName}. This supports steady movement from ${currentLevelName} to the next milestone.`
        : `Your child is currently working at ${currentLevelName}. Finley is selecting the next activities to strengthen core skills for ${nextLevelName}. This keeps challenge and confidence in balance.`;

      const spellingInsight = spellingSummary?.total
        ? `Your child practiced ${spellingSummary.total} spelling words this week, with ${spellingSummary.mastered} already mastered. Finley will review these words at just the right intervals. Spaced review helps words stick for the long run.`
        : null;

      const insights = [accuracyInsight, consistencyInsight, skillInsight];
      if (spellingInsight) insights.push(spellingInsight);
      return insights.slice(0, 3);
    } catch {
      return [];
    }
  }, [sessionAttempted, sessionAccuracy, sessionCorrect, completedActivities, minDisplay, lowestSkill, weakestSkillMastery, nextLevelName, currentLevelName, spellingSummary]);

  return (
    <motion.div {...slideUp}>
      <div className="px-6 pb-4 pt-8 text-center">
        <div className="mb-2 text-7xl">🐬</div>
        <h2 className="mb-1 font-finley-display text-4xl text-finley-primary">{msg}</h2>
        <p className="mb-5 text-base text-finley-text-light">{expired ? 'See you tomorrow, ' + name + '!' : 'Keep it up, ' + name + '!'}</p>
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="mx-auto mb-5 grid max-w-sm grid-cols-3 gap-3">
          {[{label:'Minutes',value:minDisplay,color:'var(--color-finley-primary)',bg:'rgba(108,92,231,0.1)',border:'rgba(108,92,231,0.25)'},{label:'Accuracy',value:sessionAccuracy + '%',color:'var(--color-finley-success)',bg:'rgba(0,184,148,0.1)',border:'rgba(0,184,148,0.25)'},{label:'Stars',value:sessionScore,color:'var(--color-finley-gold-dark)',bg:'rgba(254,202,87,0.14)',border:'rgba(254,202,87,0.35)'}].map((s) => (
            <motion.div key={s.label} variants={staggerChild} className="rounded-finley-lg border px-2 py-3 text-center shadow-finley-md" style={{ background: s.bg, borderColor: s.border }}>
              <div className="font-finley-display text-3xl" style={{ color: s.color }}>{s.value}</div>
              <div className="mt-1 text-xs text-finley-text-light">{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
        <div className="mb-5 flex justify-center gap-1">
          {Array.from({ length: Math.min(stars, 10) }).map((_, i) => <motion.span key={i} {...popIn} transition={{ ...popIn.transition, delay: i * 0.08 }} className="inline-block text-3xl">⭐</motion.span>)}
        </div>
        <div className="mb-5 rounded-finley-lg border-2 border-finley-text-muted/15 bg-finley-bg-card px-4 py-4 text-left shadow-finley-sm">
          <div className="mb-2 font-finley-display text-lg text-finley-text">Session Summary</div>
          <div className="grid grid-cols-2 gap-3 text-sm text-finley-text-light">
            <div>Activities completed: <strong className="text-finley-text">{completedActivities}</strong></div>
            <div>Words attempted: <strong className="text-finley-text">{sessionAttempted}</strong></div>
            <div>Words correct: <strong className="text-finley-text">{sessionCorrect}</strong></div>
            <div>Total score: <strong className="text-finley-text">{sessionScore}</strong></div>
          </div>
        </div>
        {(() => {
          if (!spellingSummary.total) return null;
          const done = spellingSummary.mastered === spellingSummary.total;
          return <div className="mb-4 flex items-center gap-3 rounded-finley-lg border-2 px-4 py-3 text-left shadow-finley-sm" style={{ background: done ? 'rgba(0,184,148,0.08)' : 'rgba(108,92,231,0.08)', borderColor: done ? 'rgba(0,184,148,0.2)' : 'rgba(108,92,231,0.2)' }}><span className="text-2xl">📝</span><div><div className="font-finley-display text-base text-finley-text">Spelling Words</div><div className="text-sm text-finley-text-light">{spellingSummary.mastered}/{spellingSummary.total} mastered this week! 🎯</div></div></div>;
        })()}
        <Btn onClick={onUnlock} full>{expired ? 'See You Tomorrow!' : 'Finish'}</Btn>
      </div>

      <AnimatePresence>
        {showNoticed && (
          <motion.div {...fadeIn} className="mt-1 border-t-2 border-finley-text-muted/15 px-6 py-5">
            <div className="mb-3 flex items-center gap-2 font-finley-display text-xl text-finley-text">
              <span>✨</span>
              <span>What Finley Noticed</span>
            </div>
            {noticedInsights.length > 0 && (
              <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-3">
                {noticedInsights.map((insight, i) => (
                  <motion.div key={i} variants={staggerChild} transition={{ delay: i * 0.2 }} className="rounded-finley-lg border-2 border-finley-text-muted/15 bg-finley-bg-card px-4 py-3 text-sm leading-6 text-finley-text-light shadow-finley-sm">
                    {insight}
                    {/* TODO: add "Mastered!" badge when pre/post-session BKT snapshots are available in this component. */}
                  </motion.div>
                ))}
              </motion.div>
            )}

            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 + noticedInsights.length * 0.2 }} className="mt-4 grid grid-cols-1 gap-3">
              <Btn onClick={onUnlock} full>Start Another Session</Btn>
              <motion.button {...tapScale} onClick={onViewDashboard || handleParentView} className="min-h-[48px] w-full rounded-finley-lg border-2 border-finley-text-muted/20 bg-finley-bg-card px-5 py-3 text-sm text-finley-text-light shadow-finley-sm">
                View Dashboard
              </motion.button>
              <div className="grid grid-cols-2 gap-2">
                <motion.button
                  {...tapScale}
                  onClick={onExtraPractice}
                  className="min-h-[44px] rounded-finley-full border bg-transparent px-[18px] py-[10px] text-[14px] font-semibold shadow-finley-sm"
                  style={{ borderColor: '#0D9488', color: '#0D9488' }}
                >
                  📚 Extra Practice
                </motion.button>
                <motion.button
                  {...tapScale}
                  onClick={onOpenStories}
                  className="min-h-[44px] rounded-finley-full border bg-transparent px-[18px] py-[10px] text-[14px] font-semibold shadow-finley-sm"
                  style={{ borderColor: '#0D9488', color: '#0D9488' }}
                >
                  🌙 Bedtime Stories
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {!parentUnlocked && <motion.button {...tapScale} onClick={handleParentView} className="mx-auto mb-4 block min-h-[48px] w-full max-w-sm rounded-finley-lg border-2 border-finley-text-muted/20 bg-finley-bg-card px-5 py-3 text-sm text-finley-text-light shadow-finley-sm">🔐 Parent View</motion.button>}

      <AnimatePresence>
        {showPinEntry && (
          <motion.div {...fadeIn} className="fixed inset-0 z-[9999] flex items-center justify-center bg-finley-bg-dark/45 px-4" onClick={() => setShowPinEntry(false)}>
            <motion.div {...scaleIn} onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-finley-xl bg-finley-bg-card px-6 py-8 text-center shadow-finley-lg">
              <PinGate pin={parentPin} onUnlock={() => { setParentUnlocked(true); setShowPinEntry(false); }} onSetPin={onSetPin} onCancel={() => setShowPinEntry(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCoaching && coachingPrompt && (
          <ParentCoachingPrompt
            prompt={coachingPrompt}
            onDismiss={() => setShowCoaching(false)}
            onLogDone={() => {
              logCoachingDone();
              setShowCoaching(false);
            }}
          />
        )}
      </AnimatePresence>

      {parentUnlocked && (
        <motion.div {...fadeIn} className="mt-2 border-t-2 border-finley-text-muted/15 px-6 py-4">
          <div className="mb-3 font-finley-display text-xl text-finley-secondary">📊 Parent Insights</div>
          {aiProfile?.parentNote && <div className="mb-3 rounded-finley-lg border-2 border-finley-accent/20 bg-finley-accent/10 px-4 py-3 shadow-finley-sm"><div className="mb-1 font-finley-display text-base text-finley-accent-dark">📝 Session Insight</div><div className="text-sm leading-6 text-finley-text-light">{aiProfile.parentNote}</div></div>}
          {aiProfile?.strengths?.length > 0 && <div className="mb-3 rounded-finley-lg border-2 border-finley-success/20 bg-finley-success/10 px-4 py-3 shadow-finley-sm"><div className="mb-1 font-finley-display text-base text-finley-success">💪 Strengths</div><div className="text-sm text-finley-text-light">{aiProfile.strengths.join(', ')}</div></div>}
          {aiProfile?.weaknesses?.length > 0 && <div className="mb-3 rounded-finley-lg border-2 border-finley-primary/20 bg-finley-primary/10 px-4 py-3 shadow-finley-sm"><div className="mb-1 font-finley-display text-base text-finley-primary">🎯 Working On</div><div className="text-sm text-finley-text-light">{aiProfile.weaknesses.join(', ')}</div></div>}
          {lowestSkill && (bkt[lowestSkill] || 0) < 0.95 && <div className="mb-3 rounded-finley-lg border-2 border-finley-secondary/20 bg-finley-secondary/10 px-4 py-3 text-sm leading-6 text-finley-text-light shadow-finley-sm">🐬 Finley is auto-targeting <strong className="text-finley-secondary">{lowestSkill.replace(/_/g, ' ')}</strong> in the next session.</div>}
          {!aiProfile && <div className="mb-3 rounded-finley-lg border-2 border-finley-text-muted/15 bg-finley-bg-card px-4 py-3 text-sm text-finley-text-light shadow-finley-sm">📊 AI session analysis will appear after more rounds.</div>}
          {canAccessStories() && (() => {
            const ts = getTodayStory(childId);
            const storyReady = ts && !ts.read;
            return storyReady ? <motion.button {...tapBounce} onClick={onOpenStories} className="mb-3 flex w-full items-center gap-3 rounded-finley-lg px-4 py-3 text-left shadow-finley-lg" style={{ background: 'linear-gradient(135deg,#0D0221,#1A0A3E)' }}><span className="text-3xl">🌙</span><div className="flex-1"><div className="font-finley-display text-lg text-white">Tonight&apos;s story is ready!</div><div className="text-sm text-white/70">Tap to read {name}&apos;s personalized bedtime story</div></div><div className="text-lg text-white/50">›</div></motion.button> : null;
          })()}
          {!expired && <motion.button {...tapScale} onClick={onUnlock} className="mx-auto block min-h-[48px] w-full max-w-sm rounded-finley-lg border-2 border-finley-text-muted/20 bg-finley-bg-card px-5 py-3 text-sm text-finley-text-light shadow-finley-sm">Finish Session</motion.button>}
        </motion.div>
      )}
    </motion.div>
  );
}
